import React, { useMemo } from 'react';
import { Dropdown } from 'components/Dropdown/Dropdown';
import { CardSizeSelector, CardSize } from 'components/CardSizeSelector/CardSizeSelector';
import SortDirectionToggle from './SortDirectionToggle';
import { SearchView, SEARCH_VIEW_OPTIONS, SHOW_LABEL_PREFIX } from 'constants/search';
import { SortOrder } from 'types/interfaces/sort';
import {
  SORT_OPTIONS,
  SORT_LABEL_PREFIX,
  getDefaultDirection,
  getOptionByKey,
} from '../sortOptions';
import { FilterToken } from '../searchState';
import FilterTokenRow from './FilterTokenRow';
import './ResultsToolbar.css';

interface ResultsToolbarProps {
  totalCount?: number;
  /** Same rule the grid uses, computed once in SearchWorkbenchPage. */
  isResultsPending: boolean;
  tokens: FilterToken[];
  onClearToken: (token: FilterToken) => void;
  onClearAll: () => void;
  sort: string;
  direction: SortOrder;
  onSortChange: (sort: string, direction: SortOrder) => void;
  lang: string;
  cardSize: CardSize;
  onCardSizeChange: (size: CardSize) => void;
  isMobile: boolean;
  /** All Prints / Unique Cards (Asana 1219353862024235, 1219902640605024). Rendered only when the backend echoes `view`. */
  view?: SearchView;
  onViewChange?: (view: SearchView) => void;
  showViewToggle?: boolean;
}

const viewLabels = SEARCH_VIEW_OPTIONS.map((o) => `${SHOW_LABEL_PREFIX}${o.label}`);

const SEARCH_CARD_SIZES: CardSize[] = ['S', 'M', 'L'];

export const ResultsToolbar: React.FC<ResultsToolbarProps> = ({
  totalCount,
  isResultsPending,
  tokens,
  onClearToken,
  onClearAll,
  sort,
  direction,
  onSortChange,
  lang,
  cardSize,
  onCardSizeChange,
  isMobile,
  view,
  onViewChange,
  showViewToggle = false,
}) => {
  // Point Value sorting is EN-only on the backend, so it is offered but disabled
  // for a non-English search rather than silently returning the wrong order.
  const isEnglishSearch = !lang || lang === 'en';
  const disabledOptions = useMemo(
    () =>
      isEnglishSearch
        ? []
        : SORT_OPTIONS.filter((o) => o.enOnly).map((o) => `${SORT_LABEL_PREFIX}${o.label}`),
    [isEnglishSearch],
  );

  const sortLabels = useMemo(
    () => SORT_OPTIONS.map((o) => `${SORT_LABEL_PREFIX}${o.label}`),
    [],
  );

  const currentSortLabel = `${SORT_LABEL_PREFIX}${getOptionByKey(sort)?.label ?? ''}`;

  const handleSortLabel = (label: string) => {
    const key = SORT_OPTIONS.find((o) => `${SORT_LABEL_PREFIX}${o.label}` === label)?.key;
    if (key) onSortChange(key, getDefaultDirection(key));
  };

  // Split rather than one string: the number carries the weight and the unit
  // recedes, which a single label cannot express.
  // The old `&& totalCount === undefined` clause meant a filter change kept showing
  // the PREVIOUS query's total instead of saying it was searching. One rule with the
  // grid now: the page decides, the toolbar renders.
  const isSearching = isResultsPending;
  const count = (totalCount ?? 0).toLocaleString();
  const unit = totalCount === 1 ? 'result' : 'results';

  return (
    <div className="results-toolbar">
      <div className="results-toolbar-primary">
        <span className="results-count" data-testid="results-count">
          {isSearching ? (
            'Searching...'
          ) : (
            <>
              {count} <span className="results-count-unit">{unit}</span>
            </>
          )}
        </span>

        {tokens.length > 0 && (
          <span className="results-toolbar-divider" aria-hidden="true" />
        )}

        <FilterTokenRow
          tokens={tokens}
          onClear={onClearToken}
          onClearAll={onClearAll}
          scrollable={isMobile}
        />
      </div>

      <div className="results-toolbar-controls">
        {showViewToggle && view && onViewChange && (
          <div className="results-sort-chip results-view-chip">
            <Dropdown
              ariaLabel="Result view"
              options={viewLabels}
              value={`${SHOW_LABEL_PREFIX}${SEARCH_VIEW_OPTIONS.find((o) => o.value === view)?.label ?? ''}`}
              onChange={(label) => {
                const next = SEARCH_VIEW_OPTIONS.find(
                  (o) => `${SHOW_LABEL_PREFIX}${o.label}` === label,
                )?.value;
                if (next) onViewChange(next);
              }}
            />
          </div>
        )}
        {/* The wrapper is the restyle hook: Dropdown is a MUI Select styled by
            inline sx and shared app-wide, so it is reskinned from the outside
            here rather than changed at the source. */}
        <div className="results-sort-chip">
          <Dropdown
            options={sortLabels}
            value={currentSortLabel}
            onChange={handleSortLabel}
            disabledOptions={disabledOptions}
          />
        </div>
        <SortDirectionToggle
          direction={direction}
          onToggle={() => onSortChange(sort, direction === 'asc' ? 'desc' : 'asc')}
        />
        <CardSizeSelector
          value={cardSize}
          onChange={onCardSizeChange}
          sizes={SEARCH_CARD_SIZES}
        />
      </div>
    </div>
  );
};

export default ResultsToolbar;
